import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Button,
    Modal, ModalDialog, ModalClose, Divider
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon      from '@mui/icons-material/ArrowBackRounded';
import DescriptionRoundedIcon    from '@mui/icons-material/DescriptionRounded';
import FileDownloadRoundedIcon   from '@mui/icons-material/FileDownloadRounded';
import CalendarMonthRoundedIcon  from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon     from '@mui/icons-material/LocationOnRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';

import DateQuickPicker from '../../components/DateQuickPicker';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetLocations, APIGetReport, APIClearMonth, API_URL, getToken, parseApiError } from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

// Генерация последних 6 месяцев для быстрого выбора
const getMonthPresets = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const from = toISO(d);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        const to = toISO(lastDay);
        const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
        months.push({ label: label.charAt(0).toUpperCase() + label.slice(1), from, to });
    }
    return months;
};

const getQuickPeriods = () => {
    const now = new Date();
    const today = toISO(now);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    return [
        { key: 'today',     label: 'Сегодня',  from: today,              to: today },
        { key: 'yesterday', label: 'Вчера',     from: toISO(yesterday),   to: toISO(yesterday) },
        { key: 'week',      label: 'Неделя',    from: toISO(weekStart),   to: today },
        { key: 'month',     label: 'Месяц',     from: toISO(monthStart),  to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


// ════════════════════════════════════════════════════════════════════
//  ReportPage (менеджер)
// ════════════════════════════════════════════════════════════════════
export default function ReportPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canExcel = hasPerm('reports.excel');

    const monthPresets = useMemo(getMonthPresets, []);
    const quickPeriods = useMemo(getQuickPeriods, []);

    // ── Справочники ───────────────────────────────────────────────
    const [locations, setLocations] = useState([]);

    // ── Параметры ─────────────────────────────────────────────────
    const [dateFrom,      setDateFrom]      = useState(monthPresets[0].from);
    const [dateTo,        setDateTo]        = useState(monthPresets[0].to);
    const [locationId,    setLocationId]    = useState('');
    const [activePreset,  setActivePreset]  = useState(monthPresets[0].label);
    const [activeQuick, setActiveQuick]     = useState('');
    const [clearModalOpen, setClearModalOpen] = useState(false);
    const [clearPreview, setClearPreview]     = useState(null);
    const [clearing, setClearing]            = useState(false);

    // ── Превью отчёта ─────────────────────────────────────────────
    const [report,     setReport]     = useState(null);
    const [loading,    setLoading]    = useState(false);
    const [downloading, setDownloading] = useState(false);

    // ── Загрузка справочников ─────────────────────────────────────
    useEffect(() => {
        (async () => {
            const locs = await APIGetLocations(token);
            if (Array.isArray(locs)) setLocations(locs);
        })();
    }, [token]);

    // ── Загрузка превью при изменении параметров ──────────────────
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        let canceled = false;

        const load = async () => {
            setLoading(true);
            const params = { date_from: dateFrom, date_to: dateTo };
            if (locationId) params.location_id = locationId;
            const data = await APIGetReport(token, params);
            if (!canceled) {
                setReport(data);
                setLoading(false);
            }
        };

        const timer = setTimeout(load, 300);
        return () => { canceled = true; clearTimeout(timer); };
    }, [token, dateFrom, dateTo, locationId]);

    // ── Быстрый выбор месяца ──────────────────────────────────────
    const selectPreset = (p) => {
        setActivePreset(p.label);
        setActiveQuick('');
        setDateFrom(p.from);
        setDateTo(p.to);
    };

    const selectQuick = (p) => {
        setActiveQuick(p.key);
        setActivePreset('');
        setDateFrom(p.from);
        setDateTo(p.to);
    };

    // ── Скачивание Excel ──────────────────────────────────────────
    const handleDownload = async () => {
        if (!dateFrom || !dateTo) { showToast('Укажите период', 'warning'); return; }

        setDownloading(true);
        try {
            const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            if (locationId) params.append('location_id', locationId);

            const url = `${API_URL}/api/finance/reports/excel/?${params.toString()}`;

            const resp = await fetch(url, {
                headers: { Authorization: `Token ${getToken()}` },
            });

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                showToast(parseApiError(err, 'Ошибка генерации отчёта'), 'danger');
                setDownloading(false);
                return;
            }

            const blob = await resp.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `report_${dateFrom}_${dateTo}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);

            showToast('Отчёт скачан');
        } catch (e) {
            showToast('Ошибка скачивания', 'danger');
        }
        setDownloading(false);
    };

    // ── Обнулить месяц ─────────────────────────────────────────
    const handleClearMonthPreview = async () => {
        // Определяем год/месяц из activePreset или dateFrom
        const d = new Date(dateFrom);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;

        setClearing(true);
        const res = await APIClearMonth(token, { year, month });
        setClearing(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка получения превью'), 'danger');
            return;
        }

        setClearPreview({ ...res, year, month });
        setClearModalOpen(true);
    };

    const handleClearMonthConfirm = async () => {
        if (!clearPreview) return;

        setClearing(true);
        const res = await APIClearMonth(token, {
            year: clearPreview.year,
            month: clearPreview.month,
            confirm: true,
        });
        setClearing(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка очистки'), 'danger');
            return;
        }

        showToast('Данные за месяц удалены');
        setClearModalOpen(false);
        setClearPreview(null);

        // Перезагрузить отчёт
        setDateFrom(dateFrom);
    };

    // ── Рендер ────────────────────────────────────────────────────
    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Excel-отчёт
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Выгрузка данных за период
                        </Typography>
                    </Box>
                </Box>

                {/* ── Настройки ─────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 2, sm: 2.5 },
                        mb: 2,
                    }}>
                        <Stack spacing={2}>
                            {/* Быстрые кнопки дат */}
                            <DateQuickPicker
                                activeKey={activeQuick}
                                label="Быстрый выбор"
                                onChange={(from, to, key) => {
                                    setActiveQuick(key);
                                    setActivePreset('');
                                    setDateFrom(from);
                                    setDateTo(to);
                                }}
                            />

                            {/* Быстрый выбор месяца */}
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    <CalendarMonthRoundedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                                    Месяц
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                    {monthPresets.map(p => (
                                        <Chip key={p.label} size="sm"
                                            variant={activePreset === p.label ? 'solid' : 'outlined'}
                                            color={activePreset === p.label ? 'primary' : 'neutral'}
                                            onClick={() => selectPreset(p)}
                                            sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, transition: 'all .15s', px: 2, py: 0.5, 
                                                ...inputSx,
                                                "&:hover": {
                                                    color: activePreset === p.label ? 'white' : 'black'
                                                }
                                            }}
                                        >
                                            {p.label}
                                        </Chip>
                                    ))}
                                </Box>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    color="danger"
                                    startDecorator={<DeleteForeverRoundedIcon sx={{ fontSize: 16 }} />}
                                    onClick={handleClearMonthPreview}
                                    loading={clearing}
                                    sx={{
                                        mt: 1, fontSize: 12, fontWeight: 700,
                                        borderColor: 'rgba(239,68,68,0.3)',
                                        color: '#EF4444',
                                        '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' },
                                    }}
                                >
                                    Обнулить месяц
                                </Button>
                            </Box>

                            {/* Ручной выбор дат */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        С
                                    </Typography>
                                    <Input type="date" size="sm" value={dateFrom}
                                        onChange={(e) => { setDateFrom(e.target.value); setActivePreset(''); setActiveQuick(''); }}
                                        sx={inputSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        По
                                    </Typography>
                                    <Input type="date" size="sm" value={dateTo}
                                        onChange={(e) => { setDateTo(e.target.value); setActivePreset(''); }}
                                        sx={inputSx}
                                    />
                                </Box>
                            </Box>

                            {/* Фильтр по точке */}
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    <LocationOnRoundedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                                    Точка <span style={{ fontWeight: 400, opacity: 0.6 }}>(необязательно)</span>
                                </Typography>
                                <Select value={locationId} onChange={(_, v) => setLocationId(v || '')}
                                    placeholder="Все точки" size="sm"
                                    sx={inputSx}
                                    slotProps={{
                                        listbox: {
                                            sx: {
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                '& .MuiOption-root': {
                                                    color: 'var(--joy-palette-text-primary)',
                                                    fontSize: 13,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(162,38,226,0.12)',
                                                        color: 'var(--joy-palette-text-primary)',
                                                    },
                                                    '&[aria-selected="true"]': {
                                                        bgcolor: 'rgba(162,38,226,0.2)',
                                                        color: 'var(--joy-palette-primary-300)',
                                                        fontWeight: 600,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <Option value="">Все точки</Option>
                                    {locations.map(l => (
                                        <Option key={l.id} value={l.id}>{l.name}</Option>
                                    ))}
                                </Select>
                            </Box>

                            {/* Кнопка скачивания */}
                            {canExcel && (
                                <Button
                                    loading={downloading}
                                    startDecorator={<FileDownloadRoundedIcon sx={{ fontSize: 20 }} />}
                                    onClick={handleDownload}
                                    sx={{
                                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                        color: '#fff', fontWeight: 700, fontSize: 15, border: 'none',
                                        boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                        '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                        py: 1.5,
                                    }}
                                >
                                    Скачать Excel
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </motion.div>

                {/* ── Превью отчёта ─────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <MemeLoader />
                    </Box>
                ) : report ? (
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }}>
                        {/* Итого */}
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            p: { xs: 2, sm: 2.5 },
                            mb: 2,
                        }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Сводка за период
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1 }}>
                                {[
                                    { label: 'Выручка',          value: report.totals?.revenue,              color: 'var(--joy-palette-primary-300)' },
                                    { label: 'Фотографов',       value: report.photographers?.length ?? 0,   color: '#818cf8', isMoney: false },
                                    { label: 'Рабочих дн.',      value: report.totals?.work_days ?? 0,       color: 'var(--joy-palette-text-primary)', isMoney: false },
                                    { label: 'Перевод',          value: report.totals?.transfer,             color: '#818cf8' },
                                    { label: 'Наличные',         value: report.totals?.cash,                 color: '#22c55e' },
                                    { label: 'Терминал',         value: report.totals?.terminal,             color: '#f59e0b' },
                                    { label: 'ЗП',               value: report.totals?.salary,               color: '#e2e2f0' },
                                    { label: 'Премии',           value: report.totals?.reward,               color: '#22c55e' },
                                    { label: 'Штрафы',           value: report.totals?.fines,                color: '#EF4444' },
                                    { label: 'Списания',         value: report.totals?.deductions,           color: '#EF4444' },
                                    { label: 'К выплате (фот.)', value: report.totals?.net_pay_photographer, color: '#22c55e' },
                                    { label: 'К выплате (адм.)', value: report.totals?.net_pay_admin,        color: '#f59e0b' },
                                ].map(s => (
                                    <Box key={s.label} sx={{ px: 1.5, py: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {s.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                                            {s.isMoney === false ? s.value : `${fmtMoney(s.value)} ₽`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Таблица фотографов */}
                        {report.photographers && report.photographers.length > 0 && (
                            <Box sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-xl)',
                                overflow: 'hidden',
                            }}>
                                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Фотографы · {report.photographers.length}
                                    </Typography>
                                </Box>

                                {report.photographers.map((ph, idx) => (
                                    <Box key={ph.employee_id} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        px: 2, py: 1.2,
                                        borderBottom: idx < report.photographers.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {ph.employee_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2 }}>
                                                {ph.work_days} дн. · выручка {fmtMoney(ph.total)} ₽
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'var(--joy-palette-primary-300)', fontVariantNumeric: 'tabular-nums' }}>
                                                {fmtMoney(ph.net_pay)} ₽
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                к выплате
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Таблица администраторов */}
                        {report.administrators && report.administrators.length > 0 ? (
                            <Box sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-xl)',
                                overflow: 'hidden',
                                mt: 2,
                            }}>
                                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Администраторы · {report.administrators.length}
                                    </Typography>
                                </Box>

                                {report.administrators.map((adm, idx) => (
                                    <Box key={adm.employee_id} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        px: 2, py: 1.2,
                                        borderBottom: idx < report.administrators.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {adm.employee_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2 }}>
                                                {adm.work_days} дн. · выручка подч. {fmtMoney(adm.subordinate_total)} ₽
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>
                                                {fmtMoney(adm.net_pay)} ₽
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                к выплате
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-xl)',
                                p: 2, mt: 2, textAlign: 'center',
                            }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 1 }}>
                                    Администраторы
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                    Нет данных
                                </Typography>
                            </Box>
                        )}
                    </motion.div>
                ) : null}
            </Box>

            {/* Модалка подтверждения обнуления */}
            <Modal open={clearModalOpen} onClose={() => setClearModalOpen(false)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 400, p: 3,
                }}>
                    <ModalClose sx={{ color: 'var(--joy-palette-text-secondary)' }} />
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Обнулить месяц
                    </Typography>
                    {clearPreview && (
                        <>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                                Будут удалены данные за {String(clearPreview.month).padStart(2, '0')}.{clearPreview.year}:
                            </Typography>
                            <Stack spacing={0.5} sx={{ mb: 2 }}>
                                {[
                                    { label: 'Чеки', count: clearPreview.counts?.orders },
                                    { label: 'Премии', count: clearPreview.counts?.rewards },
                                    { label: 'Дневные премии', count: clearPreview.counts?.daily_rewards },
                                    { label: 'Штрафы', count: clearPreview.counts?.fines },
                                    { label: 'Оклады админов', count: clearPreview.counts?.admin_salaries },
                                ].map(item => (
                                    <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                            {item.count ?? 0}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Divider sx={{ my: 1 }} />
                            <Typography sx={{ fontSize: 12, color: '#EF4444', fontWeight: 700, mb: 2 }}>
                                Это действие нельзя отменить.
                            </Typography>
                        </>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" color="neutral" onClick={() => setClearModalOpen(false)}
                            sx={{ flex: 1, color: 'var(--joy-palette-text-secondary)' }}>
                            Отмена
                        </Button>
                        <Button color="danger" loading={clearing} onClick={handleClearMonthConfirm}
                            sx={{ flex: 1 }}>
                            Подтвердить
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}